// Generates per-platform integration code for a tracking plan.

export type Platform = 'react-native' | 'android' | 'ios' | 'flutter' | 'rest';

export const PLATFORM_LABELS: Record<Platform, { label: string; icon: string; lang: string }> = {
  'react-native': { label: 'React Native', icon: 'bi-filetype-jsx', lang: 'tsx' },
  android: { label: 'Android (Kotlin)', icon: 'bi-android2', lang: 'kotlin' },
  ios: { label: 'iOS (Swift)', icon: 'bi-apple', lang: 'swift' },
  flutter: { label: 'Flutter (Dart)', icon: 'bi-phone', lang: 'dart' },
  rest: { label: 'REST API', icon: 'bi-braces', lang: 'bash' },
};

const exampleFor = (p: any): any => {
  if (p.example !== undefined && p.example !== null) return p.example;
  if (p.type === 'number') return 1;
  if (p.type === 'boolean') return true;
  return p.name === 'currency' ? 'USD' : `your_${p.name}`;
};

const propsOf = (step: any, requiredOnly = false): [string, any][] =>
  (step.properties || []).filter((p: any) => !requiredOnly || p.required).map((p: any) => [p.name, exampleFor(p)]);

const lit = (v: any, lang: Platform): string => {
  if (typeof v === 'string') return lang === 'flutter' || lang === 'react-native' ? `'${v.replace(/'/g, "\\'")}'` : `"${v.replace(/"/g, '\\"')}"`;
  if (typeof v === 'boolean') return String(v);
  if (typeof v === 'number') return String(v);
  return JSON.stringify(v);
};

const isIdentifyStep = (key: string) => key === 'signup_completed' || key === 'login';

export const trackSnippet = (step: any, platform: Platform, appKey: string, origin: string): string => {
  const key = step.step_key;
  const props = propsOf(step);
  const ident = isIdentifyStep(key);
  switch (platform) {
    case 'react-native': {
      const body = props.length ? `, {\n${props.map(([k, v]) => `  ${k}: ${lit(v, platform)},`).join('\n')}\n}` : '';
      return `CloudAyn.track('${key}'${body});` + (ident ? `\nCloudAyn.identify(user.id, { plan: user.plan }); // no emails/phones unless your privacy policy allows` : '');
    }
    case 'android': {
      const body = props.length ? `, mapOf(\n${props.map(([k, v]) => `    "${k}" to ${lit(v, platform)}`).join(',\n')}\n)` : '';
      return `CloudAyn.track("${key}"${body})` + (ident ? `\nCloudAyn.identify(user.id, mapOf("plan" to user.plan))` : '');
    }
    case 'ios': {
      const body = props.length ? `, properties: [\n${props.map(([k, v]) => `    "${k}": ${lit(v, platform)}`).join(',\n')}\n]` : '';
      return `CloudAyn.track("${key}"${body})` + (ident ? `\nCloudAyn.identify(user.id, traits: ["plan": user.plan])` : '');
    }
    case 'flutter': {
      const body = props.length ? `, {\n${props.map(([k, v]) => `  '${k}': ${lit(v, platform)},`).join('\n')}\n}` : '';
      return `CloudAyn.track('${key}'${body});` + (ident ? `\nCloudAyn.identify(user.id, {'plan': user.plan});` : '');
    }
    case 'rest':
    default: {
      const ev = {
        sent_at: '2026-11-02T10:00:05.000Z',
        batch: [{
          type: 'track', event: key, message_id: '6f1c2c1e-0d7a-4c1e-9a55-1b0f3f6f2a10',
          anonymous_id: 'b7e6f1d2-3c4a-4b5c-8d9e-0f1a2b3c4d5e', user_id: null, session_id: '0b9f8e7d-6c5b-4a39-8271-605f4e3d2c1b',
          timestamp: '2026-11-02T10:00:01.000Z', screen: null,
          properties: Object.fromEntries(props),
          context: { platform: 'android', os: 'Android', os_version: '14', app_version: '1.0.0', build: '100' },
        }],
      };
      return `curl -X POST '${origin}/api/v1/app/batch' \\\n  -H 'Content-Type: application/json' \\\n  -H 'X-App-Key: ${appKey}' \\\n  -d '${JSON.stringify(ev, null, 2)}'`;
    }
  }
};

// rnInstallUrl: this asset's private, signed download link (from /app-analytics/sdk-links).
export const installSnippet = (platform: Platform, rnInstallUrl?: string): string => {
  const notYet = (name: string) =>
    `// The ${name} SDK package is not available for download yet.\n// Use the REST API tab in the meantime — same events, same dashboard.`;
  switch (platform) {
    case 'react-native':
      return rnInstallUrl
        ? `# Private install link for this asset — do not share it publicly\nnpm install ${rnInstallUrl} @react-native-async-storage/async-storage\n# iOS\ncd ios && pod install`
        : `# Loading your private install link…`;
    case 'android':
      return notYet('Android');
    case 'ios':
      return notYet('iOS');
    case 'flutter':
      return notYet('Flutter').replace(/\/\//g, '#');
    default:
      return `# No install needed — POST JSON to the ingest API from your backend or app.`;
  }
};

export const initSnippet = (platform: Platform, appKey: string, origin: string): string => {
  const ep = origin;
  switch (platform) {
    case 'react-native':
      return `// index.js / App.tsx — once, at startup
import CloudAyn from '@cloudayn/react-native';

CloudAyn.init('${appKey}', {
  endpoint: '${ep}',
  appVersion: '1.0.0', // e.g. DeviceInfo.getVersion()
  appBuild: '100',
  debug: __DEV__,
});

// Screens (React Navigation):
// <NavigationContainer onStateChange={() => CloudAyn.screen(navigationRef.getCurrentRoute()?.name ?? 'unknown')}>`;
    case 'android':
      return `class MyApp : Application() {
    override fun onCreate() {
        super.onCreate()
        CloudAyn.init(this, "${appKey}", CloudAynOptions(
            endpoint = "${ep}",
            debug = BuildConfig.DEBUG,
        ))
    }
}
// Screens: CloudAyn.screen("ProductDetail") in onResume()`;
    case 'ios':
      return `import CloudAyn

// AppDelegate.didFinishLaunching / SwiftUI App.init()
CloudAyn.initialize(appKey: "${appKey}", options: .init(
    endpoint: "${ep}",
    debug: false
))
// Screens: CloudAyn.screen("ProductDetail") in viewDidAppear / .onAppear`;
    case 'flutter':
      return `import 'package:cloudayn_flutter/cloudayn_flutter.dart';

void main() {
  WidgetsFlutterBinding.ensureInitialized();
  CloudAyn.init('${appKey}', const CloudAynOptions(
    endpoint: '${ep}',
    appVersion: '1.0.0',
    appBuild: '100',
  ));
  runApp(const MyApp());
}
// Screens: MaterialApp(navigatorObservers: [CloudAynNavigatorObserver()])`;
    default:
      return `# Remote config (kill switch, sampling, flush interval):
curl '${origin}/api/v1/app/config' -H 'X-App-Key: ${appKey}'`;
  }
};
